import * as React from 'react';
import reactJsonView from 'react-json-view';
import { Radio } from 'antd';
import {
  Reference,
  TriggerCondition,
  ConditionContent,
  dataQueryTypeString,
  CONNECTION_TYPE
} from '@interface/index';
import { CONFLICT_RESOLVE_TYPE, GetOperationName, Operation } from '@config/index';
import { ParameterMapStore, isEqual, getMessageTemplatesSync } from '@lib/index';
import { localizedString } from '@locale/index';
import { DiffNodeRenderType, DiffNodeConfig } from './node-config';
import DiffViewer, { DiffMethod } from 'react-diff-viewer';
import { referenceUtils } from '@component/index';
import '@style/component/diff/diff-comp/index.less';

const ReactJson = reactJsonView;

interface DiffNodeCompProps {
  path: string[];
  masterData?: any;
  localData?: any;
  isArray?: boolean;
  node?: DiffNodeConfig;
  renderType?: DiffNodeRenderType;
  conflicting?: boolean;
  onResolve?: (choice: CONFLICT_RESOLVE_TYPE) => void;
}

interface DiffNodeCompState {
  resolveType?: CONFLICT_RESOLVE_TYPE;
  master?: any;
  local?: any;
}

export class DiffNodeComp extends React.Component<DiffNodeCompProps, DiffNodeCompState> {

  state = {
    resolveType: CONFLICT_RESOLVE_TYPE.LOCAL,
    master: undefined,
    local: undefined
  };

  componentDidMount() {
    this.onInit();
  }

  componentDidUpdate(prevProps: DiffNodeCompProps, prevState: DiffNodeCompState) {
    const propsDiff = !isEqual(this.props.masterData, prevProps.masterData) ||
      !isEqual(this.props.localData, prevProps.localData);

    if (propsDiff) {
      this.onInit();
    }
  }

  onInit = async () => {
    const { masterData, localData, node, isArray, renderType, conflicting } = this.props;

    let master: any = JSON.stringify(masterData);
    let local: any = JSON.stringify(localData);
    master = master && JSON.parse(master);
    local = local && JSON.parse(local);
    if (isArray) {
      if (node) {
        master = master && Object.values(master).map((item: any) =>
          this.parseNode(item, node));
        local = local && Object.values(local).map((item: any) =>
          this.parseNode(item, node));
      } else {
        master = master && Object.values(master).map((item: any) =>
          this.parseLeaf(item, renderType));
        local = local && Object.values(local).map((item: any) =>
          this.parseLeaf(item, renderType));
      }
    } else {
      if (node) {
        master = this.parseNode(master, node);
        local = this.parseNode(local, node);
      } else {
        master = this.parseLeaf(master, renderType);
        local = this.parseLeaf(local, renderType);
      }
    }

    this.setState({
      master: masterData ? master : undefined,
      local: localData ? local : undefined
    }, () => {
      if (conflicting && this.props.onResolve) {
        this.props.onResolve(this.state.resolveType);
      }
    });
  }

  parseNode = (data: any, node: DiffNodeConfig) => {

    if (data === undefined || data === null) {
      return undefined;
    }

    const result: any = {};

    for (const field of node.fields) {
      let value = data[field.key];

      if (field.node) {
        const parsedArr = [];
        if (field.isArray) {
          for (const item of value || []) {
            parsedArr.push(this.parseNode(item, field.node));
          }
          value = parsedArr;
        } else {
          value = value && this.parseNode(value, field.node);
        }
      } else {
        if (field.isArray) {
          value = value && Object.values(value).map((item: any) => this.parseLeaf(item, field.renderType));
        } else {
          value = (value !== undefined && value !== null) && this.parseLeaf(value, field.renderType) || undefined;
        }
      }

      if (value !== undefined && value !== null) {
        result[field.localeNameKey ? localizedString(field.localeNameKey) : field.name] = value;
      }
    }

    return result;
  }

  parseLeaf = (data: any, renderType: DiffNodeRenderType = DiffNodeRenderType.default) => {

    if (data === undefined || data === null) {
      return undefined;
    }

    switch (renderType) {
      case DiffNodeRenderType.referenceText:
        return data;
      case DiffNodeRenderType.boolean:
        return data ? localizedString('common_yes') : localizedString('common_no');
      case DiffNodeRenderType.trainingPhrase:
        return data.trainingPhrase;
      case DiffNodeRenderType.parameterCompanion:
        return `${data.name
          }(${localizedString(
            data.multi_value
              ? 'attribute_multi_value'
              : 'attribute_single_value'
          )
          })`;
      case DiffNodeRenderType.dataQueryName:
        const dataQuery = ParameterMapStore.get(data);
        return dataQuery && dataQuery.name || data;
      case DiffNodeRenderType.dataQueryType:
        return dataQueryTypeString(data);
      case DiffNodeRenderType.variable:
        return this.parseVariable(Array.isArray(data) ? data : [data]);
      case DiffNodeRenderType.connectionType:
        switch (data) {
          case CONNECTION_TYPE.SQL:
            return 'SQL';
          case CONNECTION_TYPE.WEBHOOK:
            return 'WEBHOOK';
          case CONNECTION_TYPE.GRAPHQL:
            return 'GRAPHQL';
          default:
            return 'UNKNOWN';
        }
      case DiffNodeRenderType.triggerCondition:
        return this.renderTriggerCondition(data);
      case DiffNodeRenderType.responseActionType:
        return localizedString(`response_type_${data}`);
      case DiffNodeRenderType.paramAction:
        return localizedString(`attribute_action_${data}`);
      case DiffNodeRenderType.frame:
        return data.name;
      case DiffNodeRenderType.prompt:
        return data.prompt;
      case DiffNodeRenderType.source:
        return data.length !== 0 ? data : 'Local';
      case DiffNodeRenderType.library:
        return data && {
          ['Org']: data.orgName,
          ['Agent']: data.name,
          ['Version']: data.originKey.commitNumber
        };
      case DiffNodeRenderType.pageOrder:
        return data.map((item: any) => {
          const param = ParameterMapStore.get(item.pageId);
          return param && param.name || item.pageId;
        });
      case DiffNodeRenderType.valueRecHard:
        return data ? localizedString('common_no') : localizedString('common_yes');
      case DiffNodeRenderType.messageTemplate:

        if (!data || data.length === 0) {
          return '';
        }

        const tempDic = getMessageTemplatesSync();
        const tmp = tempDic[data];

        if (!tmp) {
          return '';
        }

        return `${tmp.type}${tmp.flavor.length ? `(${tmp.flavor})` : ''}`;
      default:
        return data;
    }
  }

  parseVariable = (data: Reference) => {
    return (data || []).map((item: any) => item.name).join('.');
  }

  renderTriggerCondition(data: TriggerCondition) {
    let variable = this.parseVariable(data.variable);

    if (data.conditions && data.conditions.length === 1 && data.conditions[0].operationAt === 'table_row') {
      variable = `${variable}.size`;
    }

    const conditions = data.conditions && data.conditions
      .map((condition: ConditionContent) => {
        let value = '';

        if (condition.operation === Operation.IsTypeCondition) {
          const param = condition.value
            && condition.value.length
            && ParameterMapStore.get(condition.value[0]);
          value = param && param.name || value;
        } else {
          value = condition.value.join(',');
        }

        return `${GetOperationName(condition.operation)}  ${value}`;
      });

    return {
      ...data,
      variable,
      conditions
    };
  }

  render() {
    const { master, local, resolveType } = this.state;
    const { path, conflicting, renderType } = this.props;

    return (<div className="diff-comp-wrap">
      <span className="diff-path">{path.join('/')}</span>
      {/* {conflicting &&
        ((renderType === DiffNodeRenderType.boolean || (
          master !== undefined
          && this.checkNotEmpty(master)))
          || conflicting) &&
        <div className="diff-row">
          {conflicting &&
            <Radio
              style={{ marginTop: 3 }}
              checked={resolveType === CONFLICT_RESOLVE_TYPE.MASTER}
              onClick={() => {
                this.onResolveTypeChange(CONFLICT_RESOLVE_TYPE.MASTER);
              }}
            />
          }
          <span className="title" key="title-master">Master</span>
          <span className={`content master ${local ? '' : 'del'}`} >
            {this.renderData(master)}
          </span>
        </div> || ''
      }
      {conflicting &&
        ((renderType === DiffNodeRenderType.boolean ||
          (local !== undefined
            && this.checkNotEmpty(local)))
          || conflicting) &&
        <div className="diff-row">
          {conflicting &&
            <Radio
              style={{ marginTop: 3 }}
              checked={resolveType === CONFLICT_RESOLVE_TYPE.LOCAL}
              onClick={() => {
                this.onResolveTypeChange(CONFLICT_RESOLVE_TYPE.LOCAL);
              }}
            />
          }
          <span className="title" key="title-local">Local</span>
          <span className="content local" key="content-local">
            {this.renderData(local)}
          </span>
        </div> || ''
      } */}
      {
        this.diffViewer(master, local)
      }
    </div>
    );
  }

  diffViewer = (o: any, n: any) => {
    const { path, conflicting, renderType } = this.props;
    const { master, local, resolveType } = this.state;
    let oldv = o;
    let newv = n;
    if (o !== undefined && o !== null && typeof o !== 'string') {
      oldv = JSON.stringify(oldv, undefined, 2);
    }

    if (n !== undefined && n !== null && typeof n !== 'string') {
      newv = JSON.stringify(newv, undefined, 2);
    }

    // const customStyles = {
    //   diffContainer: {
    //     display: 'flex',
    //     width: '100%', // 保证容器宽度不变
    //   },
    //   diffRemoved: {
    //     flex: 1,           // 左侧自动占满一半空间
    //     paddingRight: 10,  // 可选的左右内边距
    //     overflowX: 'auto', // 超出部分自动滚动
    //   },
    //   diffAdded: {
    //     flex: 1,           // 右侧自动占满一半空间
    //     paddingLeft: 10,   // 可选的左右内边距
    //     overflowX: 'auto', // 超出部分自动滚动
    //   },
    // };

    return <DiffViewer
      compareMethod={DiffMethod.WORDS_WITH_SPACE}
      leftTitle={<span>{conflicting &&
        <Radio
          style={{ marginTop: 3 }}
          checked={resolveType === CONFLICT_RESOLVE_TYPE.MASTER}
          onClick={() => {
            this.onResolveTypeChange(CONFLICT_RESOLVE_TYPE.MASTER);
          }}
        />}Master</span>}
      rightTitle={<span>{conflicting && <Radio
        style={{ marginTop: 3 }}
        checked={resolveType === CONFLICT_RESOLVE_TYPE.LOCAL}
        onClick={() => {
          this.onResolveTypeChange(CONFLICT_RESOLVE_TYPE.LOCAL);
        }}
      />}Local</span>}
      oldValue={oldv}
      newValue={newv}
      splitView={!!conflicting}  // 设置为 true 就是左右并排模式
    // styles={customStyles}
    />;
  }

  renderData = (src: any) => {
    switch (typeof src) {
      case 'object':
        return <ReactJson
          src={src}
          name={false}
          displayObjectSize={false}
          displayDataTypes={false}
        />;
      default:
        if (this.props.renderType === DiffNodeRenderType.boolean && !src) {
          return localizedString('common_no');
        }

        return src;
    }
  }

  checkNotEmpty(data: any) {
    if (Array.isArray(data) || typeof data === 'string') {
      return data.length !== 0;
    }

    return data !== undefined && data !== null && !isEqual(data, []) && !isEqual(data, {});
  }

  onResolveTypeChange(choice: CONFLICT_RESOLVE_TYPE) {

    if (this.state.resolveType === choice) {
      return;
    }

    this.setState({ resolveType: choice }, () => {
      if (this.props.onResolve) {
        this.props.onResolve(choice);
      }
    });
  }
}
